import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { rechargeSchema, type RechargeFormValues } from '../schemas/rechargeSchema'
import { useDeliveryChannels } from '../hooks/useDeliveryChannels'
import { useCurrencies } from '../hooks/useCurrencies'
import { usePurchaseRecharge } from '../hooks/usePurchaseRecharge'

interface PurchaseRechargeDialogProps {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Llamado tras una compra exitosa (la página refresca balance + historial). */
  onSuccess?: () => void
}

/** Formulario de compra de recarga: importe + canal + moneda. */
export function PurchaseRechargeDialog({
  businessId,
  open,
  onOpenChange,
  onSuccess,
}: PurchaseRechargeDialogProps) {
  const { channels, loading: channelsLoading } = useDeliveryChannels()
  const { currencies, defaultCurrencyId, loading: currenciesLoading } = useCurrencies()

  const form = useForm<RechargeFormValues>({
    resolver: zodResolver(rechargeSchema),
    mode: 'onBlur',
    defaultValues: {
      amount: undefined,
      deliveryChannel: '',
      currencyId: undefined,
    },
  })

  // Al abrir (o cuando lleguen los catálogos), fija defaults razonables y limpia
  // errores de aperturas previas.
  useEffect(() => {
    if (!open) return
    form.reset({
      amount: undefined,
      deliveryChannel: channels[0]?.channel ?? '',
      currencyId: defaultCurrencyId ?? undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channels.length, defaultCurrencyId])

  const { purchaseRecharge, loading } = usePurchaseRecharge({
    onSuccess: () => {
      onOpenChange(false)
      onSuccess?.()
    },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    await purchaseRecharge({ businessId, ...values })
  })

  const catalogsLoading = channelsLoading || currenciesLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Comprar crédito</DialogTitle>
          <DialogDescription>
            Indica el importe de crédito a comprar. Se generará una factura y, al
            confirmarse el pago, el balance se actualizará.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Importe</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      name={field.name}
                      ref={field.ref}
                      value={field.value ?? ''}
                      onBlur={field.onBlur}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : e.target.valueAsNumber,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deliveryChannel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Canal de entrega</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={catalogsLoading || channels.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            channels.length ? 'Selecciona un canal' : 'Sin canales disponibles'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {channels.map((c) => (
                        <SelectItem key={c.id} value={c.channel}>
                          {c.channel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currencyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Moneda</FormLabel>
                  <Select
                    value={field.value != null ? String(field.value) : ''}
                    onValueChange={(v) => field.onChange(Number(v))}
                    disabled={catalogsLoading || currencies.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={currencies.length ? 'Selecciona una moneda' : 'Sin monedas'}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={loading || catalogsLoading}>
                {loading ? 'Procesando…' : 'Confirmar compra'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
